import { mainApi } from '@/lib/api';
import {
    CourseAnalytics,
    CourseEmailTemplate,
    CourseEmailTemplateResponse,
    CourseFansResponse,
} from '@/features/analytics/types/courseAnalytics.types';
import { CourseFan } from '@/types/courses';
import type { CourseEmailPayload } from './types';

// Re-export types for consumers
export type { CourseEmailPayload };

/**
 * Fetch course analytics data including metrics and chart data
 */
export async function fetchCourseAnalytics(courseId: string): Promise<CourseAnalytics> {
    if (isNaN(Number(courseId))) {
        throw new Error('Invalid course ID');
    }
    const response = await mainApi.get<CourseAnalytics>(`v1/courses/${courseId}/analytics`);
    return response.data;
}

/**
 * Fetch course fans/users with optional filters
 */
export async function fetchCourseFans(courseId: string, filterString?: string): Promise<CourseFan[]> {
    const params = filterString ? { filter_array: filterString } : undefined;

    const response = await mainApi.get<CourseFansResponse>(`v1/courses/${courseId}/fans`, { params });
    return response.data.data;
}

/**
 * Fetch email template variables for course mass email
 */
export async function fetchEmailTemplateVariables(): Promise<CourseEmailTemplate> {
    const response = await mainApi.get<CourseEmailTemplateResponse>(
        'v1/courses/email-template-variables/course-mass-email'
    );
    return response.data.course_mass_email;
}

/**
 * Send mass email to course fans
 */
export async function sendCourseMassEmail(courseId: string, payload: CourseEmailPayload): Promise<void> {
    await mainApi.post(`v1/courses/${courseId}/mass-email`, payload);
}

/**
 * Revoke course access for a user
 */
export async function revokeCourseAccess(courseId: string, userFanId: string): Promise<void> {
    await mainApi.post(`v1/courses/creator/${courseId}/revoke`, { user_fan_id: userFanId });
}

/**
 * Restore course access for a user
 */
export async function restoreCourseAccess(courseId: string, userFanId: string): Promise<void> {
    await mainApi.post(`v1/courses/creator/${courseId}/restore`, { user_fan_id: userFanId });
}

/**
 * Send course link to a user via email (used from course analytics context)
 */
export async function sendCourseLink(slug: string): Promise<void> {
    await mainApi.post(`v1/courses/creator/new-link/${slug}`, {}, { params: { send_link: 1 } });
}

/**
 * Resend course link to a user via email (used from payment/income context)
 */
export async function sendCourseLinkByPayment(paymentId: string): Promise<void> {
    await mainApi.post(`v1/courses/creator/payment/new-link/${paymentId}`);
}

/**
 * Generate a new course link and return the URL
 */
export async function generateCourseLink(slug: string): Promise<string> {
    const response = await mainApi.post<{ course_link: string }>(
        `v1/courses/creator/new-link/${slug}`,
        {},
        { params: { send_link: 0 } }
    );
    return response.data.course_link;
}
